import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ensureCsrfCookie, xsrfHeaders } from '../services/authApi';
import {
  agentChat,
  createProject,
  createResearchDraft,
  createUserAgent,
  generateResearchDraft,
  getProject,
  getProjectChatHistory,
  getProjectWhatsNew,
  ingestWikipedia,
  listMaterials,
  listProjects,
  listResearchArticles,
  listUserAgents,
  publishResearchArticle,
  refreshProject,
  updateResearchDraft,
  uploadMaterial,
} from '../services/agentApi';
import { getMyAuthorProfile } from '../services/authorApi';
import { resolveApiBaseUrl } from '../utils/apiBase';
import { normalizeMarkdownContent } from '../utils/markdownContent';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatArticleCategories } from '../utils/articleCategories';
import { formatLocalDate } from '../utils/formatDate';
import PageHeader from '../components/PageHeader';
import '../styles/AuthorMarkdown.css';
import './ResearchPage.css';

const TABS = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'projects', label: 'My Projects' },
  { id: 'agents', label: 'My Agents' },
  { id: 'materials', label: 'Materials' },
];

const AGENT_CHAT_TIMEOUT_MS = 150000;
const MAX_ARTICLES_FOR_ANALYSIS = 5;

const ResearchPage = () => {
  useDocumentTitle('Research');
  const navigate = useNavigate();
  const { authRequired, isAuthenticated, loading: authLoading } = useAuth();
  const API_BASE_URL = resolveApiBaseUrl();
  const chatInFlightRef = useRef(false);

  useEffect(() => {
    if (!authLoading && authRequired && !isAuthenticated) {
      navigate('/login', { replace: true, state: { from: '/research' } });
    }
  }, [authLoading, authRequired, isAuthenticated, navigate]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [activeQuery, setActiveQuery] = useState('');

  const [chatMessage, setChatMessage] = useState('');
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatTurns, setChatTurns] = useState([]);
  const [isChatting, setIsChatting] = useState(false);
  const [chatError, setChatError] = useState(null);

  const [activeTab, setActiveTab] = useState('workspace');
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activeProjectTitle, setActiveProjectTitle] = useState('');
  const [projects, setProjects] = useState([]);
  const [userAgents, setUserAgents] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [hubError, setHubError] = useState(null);
  const [selectedAgentSlug, setSelectedAgentSlug] = useState('research');
  const [wikiUrl, setWikiUrl] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [newProjectTitle, setNewProjectTitle] = useState('');

  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [editorSessionId, setEditorSessionId] = useState(null);
  const [includeArticles, setIncludeArticles] = useState(true);
  const [includeAnalystThread, setIncludeAnalystThread] = useState(false);
  const [includeMaterials, setIncludeMaterials] = useState(false);
  const [lastDraftContext, setLastDraftContext] = useState(null);
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [citationsOpen, setCitationsOpen] = useState(true);
  const [isDrafting, setIsDrafting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaveMessage, setDraftSaveMessage] = useState(null);
  const [draftError, setDraftError] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState(null);
  const [publishedArticleUrl, setPublishedArticleUrl] = useState(null);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [slugModalOpen, setSlugModalOpen] = useState(false);
  const [authorDisplayName, setAuthorDisplayName] = useState('');
  const [authorSlugInput, setAuthorSlugInput] = useState('');

  const [projectNewCounts, setProjectNewCounts] = useState({});
  const [whatsNewPanel, setWhatsNewPanel] = useState(null);
  const [whatsNewLoading, setWhatsNewLoading] = useState(false);
  const [deltaSummary, setDeltaSummary] = useState(null);
  const [isSummarizingDelta, setIsSummarizingDelta] = useState(false);
  const [deltaError, setDeltaError] = useState(null);
  const deltaSessionIdRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    Promise.all([
      listProjects().catch(() => []),
      listUserAgents().catch(() => []),
      listMaterials().catch(() => []),
      getMyAuthorProfile().catch(() => null),
      listResearchArticles({ status: 'draft', size: 20 }).catch(() => ({ content: [] })),
    ]).then(([p, a, m, profile, draftsPage]) => {
      setProjects(p);
      setUserAgents(a);
      setMaterials(m);
      setSavedDrafts(draftsPage.content || []);
      if (profile) {
        setAuthorProfile(profile);
        setAuthorDisplayName(profile.displayName || profile.principalName || '');
        setAuthorSlugInput(profile.authorSlug || profile.suggestedSlug || '');
      }
    });
  }, [isAuthenticated]);

  const refreshDrafts = async () => {
    const draftsPage = await listResearchArticles({ status: 'draft', size: 20 }).catch(() => ({
      content: [],
    }));
    setSavedDrafts(draftsPage.content || []);
  };

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'projects' || projects.length === 0) return;
    Promise.all(
      projects.map((p) =>
        getProjectWhatsNew(p.id)
          .then((w) => [p.id, w.newCount])
          .catch(() => [p.id, 0])
      )
    ).then((entries) => setProjectNewCounts(Object.fromEntries(entries)));
  }, [isAuthenticated, activeTab, projects]);

  useEffect(() => {
    if (!isAuthenticated || !activeProjectId) return;
    getProjectWhatsNew(activeProjectId)
      .then((w) =>
        setProjectNewCounts((prev) => ({ ...prev, [activeProjectId]: w.newCount }))
      )
      .catch(() => {});
  }, [isAuthenticated, activeProjectId]);

  const loadHub = async () => {
    setProjects(await listProjects().catch(() => []));
    setUserAgents(await listUserAgents().catch(() => []));
    setMaterials(await listMaterials().catch(() => []));
  };

  const handleSaveProject = async () => {
    if (!activeQuery && !newProjectTitle.trim()) return;
    setHubError(null);
    try {
      const created = await createProject({
        title: newProjectTitle.trim() || activeQuery,
        queryText: activeQuery || searchQuery,
        retrievalSnapshot: {
          corpus: 'global',
          articleIds: [...selectedIds],
        },
        agentDefinitionId: resolveAgentDefinitionId(),
        chatSessionId: chatSessionId || undefined,
        agentSlug: selectedAgentSlug,
      });
      setActiveProjectId(created.id);
      setActiveProjectTitle(created.title);
      setNewProjectTitle('');
      await loadHub();
      setActiveTab('projects');
    } catch (e) {
      setHubError(e.message);
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgentName.trim()) return;
    const slug = newAgentName.trim().toLowerCase().replace(/\s+/g, '-').slice(0, 48);
    setHubError(null);
    try {
      await createUserAgent({
        slug,
        displayName: newAgentName.trim(),
        retrievalConfig: {
          corpus: 'global',
          articleIds: [...selectedIds],
        },
      });
      setNewAgentName('');
      await loadHub();
    } catch (e) {
      setHubError(e.message);
    }
  };

  const handleWikipedia = async () => {
    if (!wikiUrl.trim()) return;
    setHubError(null);
    try {
      await ingestWikipedia(wikiUrl.trim());
      setWikiUrl('');
      await loadHub();
    } catch (e) {
      setHubError(e.message);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHubError(null);
    try {
      await uploadMaterial(file, file.name);
      await loadHub();
    } catch (err) {
      setHubError(err.message);
    }
  };

  const resolveAgentDefinitionId = () => {
    if (selectedAgentSlug === 'research') return undefined;
    return userAgents.find((a) => a.slug === selectedAgentSlug)?.id;
  };

  const applyChatHistory = (history) => {
    if (!history?.messages?.length) {
      return;
    }
    if (history.sessionId) {
      setChatSessionId(history.sessionId);
    }
    setChatTurns(
      history.messages.map((m) => ({
        role: m.role,
        content: m.content,
        citations: [],
      }))
    );
  };

  const resetChat = () => {
    setChatTurns([]);
    setChatSessionId(null);
    setChatError(null);
    setChatMessage('');
  };

  const mapArticles = (articles) =>
    articles.map((article) => ({
      id: article.id,
      title: article.title,
      source: article.source || 'Unknown',
      date: article.publishedAt ? formatLocalDate(article.publishedAt) : '—',
      categories: formatArticleCategories(article),
      url: article.url,
      summary: stripHtml(article.summary || '').slice(0, 220),
    }));

  const runSearch = async (query, preferredIds, { resetChatOnStart = true } = {}) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    if (resetChatOnStart) {
      resetChat();
    }

    setIsSearching(true);
    setSearchError(null);
    setHasSearched(true);
    setActiveQuery(trimmed);
    setSelectedIds(new Set());

    try {
      const params = new URLSearchParams({
        page: '0',
        size: '10',
        search: trimmed,
        semantic: 'true',
      });
      const response = await fetch(`${API_BASE_URL}/feed?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Feed search failed (${response.status})`);
      }
      const data = await response.json();
      const mapped = mapArticles(data.content || []);
      setSearchResults(mapped);
      if (preferredIds?.length) {
        const idSet = new Set(preferredIds);
        const matched = mapped.filter((a) => idSet.has(a.id)).map((a) => a.id);
        setSelectedIds(new Set(matched.length > 0 ? matched : mapped.map((a) => a.id)));
      } else {
        setSelectedIds(new Set(mapped.map((a) => a.id)));
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setSearchError(error.message || 'Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleOpenProject = async (projectId) => {
    setHubError(null);
    try {
      const project = await getProject(projectId);
      let agentSlug = 'research';
      if (project.agentDefinitionId) {
        const agents = userAgents.length ? userAgents : await listUserAgents().catch(() => []);
        if (!userAgents.length) setUserAgents(agents);
        const linked = agents.find((a) => a.id === project.agentDefinitionId);
        if (linked) {
          agentSlug = linked.slug;
          setSelectedAgentSlug(linked.slug);
        }
      }
      setActiveProjectId(project.id);
      setActiveProjectTitle(project.title);
      setSearchQuery(project.queryText || '');
      setNewProjectTitle('');
      setActiveTab('workspace');
      resetChat();
      await runSearch(project.queryText || '', project.retrievalSnapshot?.articleIds, {
        resetChatOnStart: false,
      });
      const history = await getProjectChatHistory(project.id, agentSlug);
      applyChatHistory(history);
    } catch (e) {
      setHubError(e.message);
    }
  };

  const handleSearch = async () => {
    setActiveProjectId(null);
    setActiveProjectTitle('');
    await runSearch(searchQuery);
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const buildAnalysisPrompt = (customMessage) => {
    if (customMessage?.trim()) {
      return customMessage.trim();
    }
    const selected = searchResults
      .filter((a) => selectedIds.has(a.id))
      .slice(0, MAX_ARTICLES_FOR_ANALYSIS);
    const lines = selected.map(
      (a, i) => `${i + 1}. ${a.title} (${a.source}) — id: ${a.id}`
    );
    return [
      `I searched the archive for: "${activeQuery}".`,
      selected.length > 0
        ? `Please analyze these ${selected.length} article(s) and summarize key themes, disagreements, and what matters most:`
        : 'Please analyze what the archive likely contains on this topic and suggest how to refine the search.',
      ...lines,
    ].join('\n');
  };

  const handleAgentChat = async (messageOverride, displayMessage) => {
    const message = messageOverride ?? chatMessage;
    if (!message.trim() && !activeQuery) return;
    if (chatInFlightRef.current) return;

    chatInFlightRef.current = true;
    setIsChatting(true);
    setChatError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), AGENT_CHAT_TIMEOUT_MS);

    const prompt = buildAnalysisPrompt(message);
    const userLabel =
      displayMessage?.trim() ||
      (messageOverride !== undefined && messageOverride !== '' ? messageOverride : chatMessage).trim() ||
      prompt;

    try {
      await ensureCsrfCookie();
      const payload = {
        message: prompt,
        ...(chatSessionId ? { sessionId: chatSessionId } : {}),
        ...(activeProjectId ? { researchProjectId: activeProjectId } : {}),
      };
      const response = await fetch(`${API_BASE_URL}/v1/ai/agents/${selectedAgentSlug}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...xsrfHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `Agent chat failed (${response.status})`);
      }
      const data = await response.json();
      setChatSessionId(data.sessionId);
      setChatTurns((prev) => [
        ...prev,
        { role: 'user', content: userLabel },
        {
          role: 'assistant',
          content: data.response,
          citations: data.citations || [],
        },
      ]);
      setChatMessage('');
    } catch (error) {
      console.error('Agent chat error:', error);
      if (error.name === 'AbortError') {
        setChatError(
          'Analysis timed out after 2½ minutes. Try fewer selected articles or a shorter question.'
        );
      } else if (error.message === 'Failed to fetch') {
        setChatError(
          'Lost connection while waiting for the analyst (often a network or proxy timeout). Please wait a few seconds and try again.'
        );
      } else {
        setChatError(error.message);
      }
    } finally {
      clearTimeout(timeoutId);
      chatInFlightRef.current = false;
      setIsChatting(false);
    }
  };

  const canGenerateDraft = () => {
    const query = (activeQuery || searchQuery || '').trim();
    const count = searchResults.filter((a) => selectedIds.has(a.id)).length;
    if (!query) return false;
    if (includeArticles && count < 1) return false;
    if (!includeArticles && !includeAnalystThread && !includeMaterials) return false;
    if (includeAnalystThread && chatTurns.length === 0) return false;
    if (includeMaterials && materials.length === 0) return false;
    return true;
  };

  const runDraftGeneration = async () => {
    setIsDrafting(true);
    setDraftError(null);
    setPublishMessage(null);
    setRegenerateConfirmOpen(false);
    try {
      const sourceIds = includeArticles
        ? searchResults.filter((a) => selectedIds.has(a.id)).map((a) => a.id)
        : [];
      const data = await generateResearchDraft({
        query: activeQuery || searchQuery,
        sourceArticleIds: sourceIds,
        includeArticles,
        includeAnalystThread,
        includeMaterials,
        researchProjectId: activeProjectId || undefined,
        researchSessionId: chatSessionId || undefined,
        editorSessionId: editorSessionId || undefined,
      });
      setEditorSessionId(data.sessionId);
      setLastDraftContext(data.context || null);
      const body = data.content || '';
      setDraftContent(body);
      setDraftTitle(parseDraftTitle(body, `Research: ${activeQuery}`));
    } catch (e) {
      setDraftError(e.message);
    } finally {
      setIsDrafting(false);
    }
  };

  const handleDraftArticle = async () => {
    if (!canGenerateDraft()) return;
    if (draftContent.trim()) {
      setRegenerateConfirmOpen(true);
      return;
    }
    await runDraftGeneration();
  };

  const handleConfirmRegenerate = async () => {
    await runDraftGeneration();
  };

  const parseDraftTitle = (markdown, fallback) => {
    const firstLine = markdown.trim().split('\n')[0] || '';
    if (firstLine.startsWith('# ')) {
      return firstLine.slice(2).trim();
    }
    return fallback || `Research: ${activeQuery || 'Draft'}`;
  };

  const buildDraftPayload = () => {
    const sourceIds = searchResults.filter((a) => selectedIds.has(a.id)).map((a) => a.id);
    return {
      title: draftTitle.trim(),
      content: draftContent.trim(),
      sourceArticleIds: sourceIds,
      researchQuery: activeQuery || searchQuery || undefined,
      researchProjectId: activeProjectId || lastDraftContext?.researchProjectId || undefined,
      editorSessionId: editorSessionId || lastDraftContext?.editorSessionId || undefined,
      includeArticles,
      includeAnalystThread,
      includeMaterials,
      includedChatTurnCount: lastDraftContext?.includedChatTurnCount,
      promptVersion: lastDraftContext?.promptVersion || 'cc-20-v1',
    };
  };

  const persistDraft = async () => {
    const payload = buildDraftPayload();
    if (currentDraftId) {
      return updateResearchDraft(currentDraftId, payload);
    }
    const created = await createResearchDraft(payload);
    setCurrentDraftId(created.id);
    return created;
  };

  const handleSaveDraft = async () => {
    if (!draftTitle.trim() || !draftContent.trim()) return;
    setDraftError(null);
    setDraftSaveMessage(null);
    setIsSavingDraft(true);
    try {
      const saved = await persistDraft();
      setDraftSaveMessage(`Draft saved${saved.title ? `: "${saved.title}"` : ''}.`);
      await refreshDrafts();
    } catch (e) {
      setDraftError(e.message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleLoadDraft = (draft) => {
    setCurrentDraftId(draft.id);
    setDraftTitle(draft.title || '');
    setDraftContent(draft.content || '');
    setDraftError(null);
    setDraftSaveMessage(null);
    setPublishMessage(null);
    setPublishedArticleUrl(null);
    setActiveTab('workspace');
  };

  const handleNewDraft = () => {
    setCurrentDraftId(null);
    setDraftTitle('');
    setDraftContent('');
    setLastDraftContext(null);
    setDraftSaveMessage(null);
    setPublishMessage(null);
    setPublishedArticleUrl(null);
  };

  const runPublish = async (publishOptions = {}) => {
    const draft = await persistDraft();
    const result = await publishResearchArticle(draft.id, publishOptions);
    const article = result.article || result;
    const slug =
      result.authorSlug ||
      publishOptions.authorSlug ||
      authorProfile?.authorSlug ||
      authorSlugInput;
    setAuthorProfile((prev) =>
      prev
        ? {
            ...prev,
            authorSlug: slug,
            displayName: result.displayName || prev.displayName,
          }
        : prev
    );
    setPublishMessage(`Published "${article.title}".`);
    setCurrentDraftId(null);
    await refreshDrafts();
    if (slug && article.id) {
      setPublishedArticleUrl(`/authors/${slug}/${article.id}`);
    } else if (slug) {
      setPublishedArticleUrl(`/authors/${slug}`);
    }
  };

  const handlePublishDraft = async () => {
    if (!draftTitle.trim() || !draftContent.trim()) return;
    setDraftError(null);
    setPublishMessage(null);
    setPublishedArticleUrl(null);

    if (!authorProfile?.authorSlug) {
      setSlugModalOpen(true);
      return;
    }

    setIsPublishing(true);
    try {
      await runPublish();
    } catch (e) {
      setDraftError(e.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleConfirmSlugPublish = async () => {
    if (!authorSlugInput.trim()) {
      setDraftError('Author slug is required on first publish.');
      return;
    }
    if (!draftTitle.trim() || !draftContent.trim()) {
      setDraftError(null);
      setSlugModalOpen(false);
      setActiveTab('workspace');
      return;
    }
    setIsPublishing(true);
    setDraftError(null);
    setPublishMessage(null);
    setPublishedArticleUrl(null);
    try {
      await runPublish({
        displayName: authorDisplayName.trim() || undefined,
        authorSlug: authorSlugInput.trim(),
      });
      setSlugModalOpen(false);
    } catch (e) {
      setDraftError(e.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveSlugPreview = () => {
    if (!authorSlugInput.trim()) {
      setDraftError('Enter a channel slug (e.g. admin or jane-analyst).');
      return;
    }
    setDraftError(null);
    setSlugModalOpen(false);
  };

  const handleAnalyzeResults = () => {
    const label = `Analyze ${selectedCount} selected article(s) for "${activeQuery}"`;
    handleAgentChat('', label);
  };

  const openWhatsNewPanel = async (project) => {
    setWhatsNewLoading(true);
    setDeltaSummary(null);
    setDeltaError(null);
    setWhatsNewPanel({
      projectId: project.id,
      projectTitle: project.title,
      queryText: project.queryText,
      newCount: projectNewCounts[project.id] || 0,
      articles: [],
      loading: true,
    });
    try {
      const data = await getProjectWhatsNew(project.id);
      setWhatsNewPanel({
        projectId: project.id,
        projectTitle: project.title,
        queryText: project.queryText,
        newCount: data.newCount,
        articles: data.articles || [],
        loading: false,
      });
      setProjectNewCounts((prev) => ({ ...prev, [project.id]: data.newCount }));
    } catch (e) {
      setHubError(e.message);
      setWhatsNewPanel(null);
    } finally {
      setWhatsNewLoading(false);
    }
  };

  const handleMarkWhatsNewSeen = async (projectId) => {
    setHubError(null);
    try {
      await refreshProject(projectId);
      setProjectNewCounts((prev) => ({ ...prev, [projectId]: 0 }));
      if (whatsNewPanel?.projectId === projectId) {
        setWhatsNewPanel((prev) => (prev ? { ...prev, newCount: 0, articles: [] } : null));
      }
      await loadHub();
    } catch (e) {
      setHubError(e.message);
    }
  };

  const buildDeltaSummaryPrompt = (panel) => {
    const lines = (panel.articles || []).map(
      (a, i) => `${i + 1}. ${a.title} (${a.source || 'Unknown'}) — id: ${a.id}`
    );
    return [
      `Research project: "${panel.projectTitle}"`,
      `Original query: "${panel.queryText || ''}"`,
      `Since the analyst last reviewed this project, ${panel.newCount} new article(s) appeared in the archive.`,
      'Summarize only what is new — key developments, how they relate to the project topic, and what might warrant follow-up.',
      'Keep the answer short (roughly 150–250 words) in Markdown with bullet points where helpful.',
      '',
      'New articles:',
      ...lines,
    ].join('\n');
  };

  const handleSummarizeDelta = async () => {
    if (!whatsNewPanel?.articles?.length) return;
    setIsSummarizingDelta(true);
    setDeltaError(null);
    try {
      const data = await agentChat(
        'research',
        buildDeltaSummaryPrompt(whatsNewPanel),
        deltaSessionIdRef.current,
        whatsNewPanel.projectId
      );
      deltaSessionIdRef.current = data.sessionId;
      setDeltaSummary(data.response || '');
    } catch (e) {
      setDeltaError(e.message);
    } finally {
      setIsSummarizingDelta(false);
    }
  };

  const renderWhatsNewPanel = () => {
    if (!whatsNewPanel) return null;
    const { projectTitle, newCount, articles, loading } = whatsNewPanel;
    return (
      <div className="whats-new-panel" role="region" aria-label="What's new">
        <h4>
          What&apos;s new — {projectTitle}
          {newCount > 0 && <span className="whats-new-badge">{newCount}</span>}
        </h4>
        {loading ? (
          <p className="research-status" role="status">
            Loading new articles…
          </p>
        ) : newCount === 0 ? (
          <p className="research-hint">No new articles since your last visit.</p>
        ) : (
          <>
            <p className="research-hint">
              {newCount} new article(s) since you last marked this project as seen.
            </p>
            <ul className="whats-new-articles">
              {articles.map((a) => (
                <li key={a.id}>
                  <Link to={`/article/${a.id}`}>{a.title}</Link>
                  <div className="whats-new-meta">
                    {a.source || 'Unknown'}
                    {a.publishedAt ? ` · ${formatLocalDate(a.publishedAt)}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="whats-new-actions">
          {newCount > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={handleSummarizeDelta}
              disabled={isSummarizingDelta}
            >
              {isSummarizingDelta ? 'Summarizing…' : 'Summarize what\u2019s new'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleMarkWhatsNewSeen(whatsNewPanel.projectId)}
          >
            Mark as seen
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              setWhatsNewPanel(null);
              setDeltaSummary(null);
              setDeltaError(null);
            }}
          >
            Close
          </button>
        </div>
        {deltaError && <p className="research-error">{deltaError}</p>}
        {deltaSummary && (
          <div className="delta-summary">
            <h5>Delta summary</h5>
            <div className="delta-summary-body">
              <ReactMarkdown>{deltaSummary}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    );
  };

  const selectedCount = searchResults.filter((a) => selectedIds.has(a.id)).length;
  const citationSources = searchResults.filter((a) => selectedIds.has(a.id));
  const hasAssistantReply = chatTurns.some((t) => t.role === 'assistant');
  const activeProjectNewCount = activeProjectId ? projectNewCounts[activeProjectId] : 0;
  const workspaceSplit = hasSearched || searchResults.length > 0;
  const showDraftSection =
    hasSearched && (selectedCount > 0 || chatTurns.length > 0 || materials.length > 0);
  const draftReady = canGenerateDraft();

  return (
    <div className="research-page">
      <div className="container">
        <PageHeader
          title="My Research"
          actions={
            <Link to="/settings/profile" className="btn btn-sm btn-outline-secondary research-profile-link">
              Author profile
            </Link>
          }
        >
          <nav className="research-tabs" aria-label="Research sections">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`research-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          {hubError && <p className="research-error">{hubError}</p>}
        </PageHeader>

        {activeTab === 'projects' && (
          <section className="research-panel">
            <h3>Saved research projects</h3>
            <div className="search-form">
              <input
                className="form-control"
                placeholder="Project title"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
              />
              <button className="btn btn-primary" onClick={handleSaveProject}>
                Save current search as project
              </button>
            </div>
            <ul className="results-list">
              {projects.map((p) => (
                <li key={p.id} className="result-item project-item">
                  <div className="result-body">
                    <strong>
                      {p.title}
                      {projectNewCounts[p.id] > 0 && (
                        <span className="whats-new-badge" title="New articles since last visit">
                          {projectNewCounts[p.id]}
                        </span>
                      )}
                    </strong>
                    <p className="result-meta">{p.queryText}</p>
                    <div className="project-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => handleOpenProject(p.id)}
                      >
                        Open in workspace
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openWhatsNewPanel(p)}
                        disabled={whatsNewLoading && whatsNewPanel?.projectId === p.id}
                      >
                        {whatsNewLoading && whatsNewPanel?.projectId === p.id
                          ? 'Loading…'
                          : "What's new"}
                      </button>
                    </div>
                    {whatsNewPanel?.projectId === p.id && renderWhatsNewPanel()}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {activeTab === 'agents' && (
          <section className="research-panel">
            <h3>My custom agents</h3>
            <div className="search-form">
              <input
                className="form-control"
                placeholder="Agent display name"
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
              />
              <button className="btn btn-primary" onClick={handleCreateAgent}>
                Create from current selection
              </button>
            </div>
            <label className="research-hint">
              Chat agent:{' '}
              <select
                value={selectedAgentSlug}
                onChange={(e) => setSelectedAgentSlug(e.target.value)}
              >
                <option value="research">Research (default)</option>
                {userAgents.map((a) => (
                  <option key={a.id} value={a.slug}>
                    {a.displayName}
                  </option>
                ))}
              </select>
            </label>
            <ul>
              {userAgents.map((a) => (
                <li key={a.id}>
                  {a.displayName} <code>{a.slug}</code>
                </li>
              ))}
            </ul>
          </section>
        )}

        {activeTab === 'materials' && (
          <section className="research-panel">
            <h3>Supplemental materials</h3>
            <div className="search-form">
              <input
                className="form-control"
                placeholder="Wikipedia URL"
                value={wikiUrl}
                onChange={(e) => setWikiUrl(e.target.value)}
              />
              <button className="btn btn-outline-primary" onClick={handleWikipedia}>
                Add Wikipedia
              </button>
            </div>
            <p className="research-hint">
              Upload text, PDF, Word (.docx), Excel (.xlsx/.xls), CSV, JSON, HTML, and other
              text-based files.
            </p>
            <input type="file" onChange={handleFileUpload} className="form-control mt-2" />
            <ul>
              {materials.map((m) => (
                <li key={m.id}>
                  {m.title} <span className="text-muted">({m.sourceType})</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {activeTab === 'workspace' && (
        <div className="research-interface">
          {activeProjectTitle && (
            <p className="research-project-banner">
              Project: <strong>{activeProjectTitle}</strong>
              {activeProjectNewCount > 0 && (
                <span className="whats-new-badge" title="New articles since last visit">
                  {activeProjectNewCount}
                </span>
              )}
            </p>
          )}
          <ol className="research-steps" aria-label="Research steps">
            <li className={hasSearched ? 'done' : 'active'}>1. Find articles</li>
            <li className={searchResults.length > 0 ? 'active' : ''}>2. Review sources</li>
            <li className={hasAssistantReply ? 'done' : ''}>3. Get analysis</li>
            <li className={draftContent ? 'done' : selectedCount > 0 ? 'active' : ''}>4. Draft & publish</li>
          </ol>
          <div className="research-workspace-grid">
            <aside className="workspace-sidebar">
              <section className="research-panel">
                <h4>Configuration</h4>
                <div className="workspace-config-row">
                  <label htmlFor="workspace-agent">Analyst agent</label>
                  <select
                    id="workspace-agent"
                    value={selectedAgentSlug}
                    onChange={(e) => setSelectedAgentSlug(e.target.value)}
                  >
                    <option value="research">Research (default)</option>
                    {userAgents.map((a) => (
                      <option key={a.id} value={a.slug}>
                        {a.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="workspace-stat">
                  {selectedCount} source(s) selected
                  {activeQuery ? ` · "${activeQuery}"` : ''}
                </p>
                {activeProjectId && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mt-2"
                    onClick={() =>
                      openWhatsNewPanel({
                        id: activeProjectId,
                        title: activeProjectTitle,
                        queryText: activeQuery || searchQuery,
                      })
                    }
                    disabled={whatsNewLoading}
                  >
                    What&apos;s new
                    {activeProjectNewCount > 0 ? ` (${activeProjectNewCount})` : ''}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={() => setActiveTab('projects')}
                >
                  Save as project
                </button>
                <div className="workspace-channel mt-2">
                  <h4 className="workspace-channel-title">Author channel</h4>
                  {authorProfile?.authorSlug ? (
                    <p className="workspace-stat">
                      <Link to={`/authors/${authorProfile.authorSlug}`}>
                        /authors/{authorProfile.authorSlug}
                      </Link>
                    </p>
                  ) : (
                    <>
                      <p className="workspace-stat">
                        Choose your public URL on first publish.
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setSlugModalOpen(true)}
                      >
                        Set up channel URL
                      </button>
                    </>
                  )}
                </div>
              </section>
              {whatsNewPanel && activeProjectId === whatsNewPanel.projectId && renderWhatsNewPanel()}
            </aside>
            <div className={`workspace-main${workspaceSplit ? ' split-panels' : ''}`}>
          <section className="research-panel search-section workspace-panel-search">
            <div className="panel-label">Step 1 — Find articles</div>
            <h3>Search the archive</h3>
            <p className="research-hint">
              Semantic search — use natural language (e.g. &ldquo;chip export controls&rdquo;).
            </p>
            <div className="search-form">
              <input
                type="text"
                className="form-control"
                placeholder="What topic do you want to research?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
              >
                {isSearching ? 'Searching…' : 'Find articles'}
              </button>
            </div>
          </section>

          {(hasSearched || searchResults.length > 0) && (
            <section className="research-panel results-section workspace-panel-sources">
              <div className="panel-label">Step 2 — Review sources</div>
              <h3>
                Articles
                {searchResults.length > 0 && (
                  <span className="result-count"> ({searchResults.length} found)</span>
                )}
              </h3>
              {searchError && <p className="research-error">{searchError}</p>}
              {searchResults.length > 0 ? (
                <>
                  <p className="research-hint">
                    Select sources for the analyst (up to {MAX_ARTICLES_FOR_ANALYSIS} used per
                    analysis).
                  </p>
                  <div className="results-list">
                    {searchResults.map((result) => (
                      <label key={result.id} className="result-item">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.has(result.id)}
                          onChange={() => toggleSelection(result.id)}
                        />
                        <div className="result-body">
                          <h5>
                            <Link to={`/article/${result.id}`}>{result.title}</Link>
                          </h5>
                          <p className="result-meta">
                            {[result.source, result.date, result.categories]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                          {result.summary && (
                            <p className="result-summary">{result.summary}</p>
                          )}
                          {result.url && (
                            <a
                              href={result.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="result-external"
                            >
                              Original source ↗
                            </a>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="analyze-bar">
                    <button
                      className="btn btn-success"
                      onClick={handleAnalyzeResults}
                      disabled={isChatting || selectedCount === 0}
                    >
                      {isChatting
                        ? 'Analyzing…'
                        : `Ask analyst about ${selectedCount} selected`}
                    </button>
                  </div>
                </>
              ) : (
                !isSearching && (
                  <p className="research-hint">
                    No articles matched &ldquo;{activeQuery}&rdquo;. Try shorter keywords or a
                    different angle, then ask the analyst anyway.
                  </p>
                )
              )}
            </section>
          )}

          <section className="research-panel article-creation workspace-panel-analyst">
            <div className="panel-label">Step 3 — Analyst</div>
            <h3>Ask the analyst</h3>
            <p className="research-hint">
              Synthesizes sources, cites articles, and answers follow-ups. Analysis may take
              10–60 seconds — keep this tab open while waiting.
            </p>
            {isChatting && (
              <p className="research-status" role="status">
                Analyst is working… this can take up to a minute.
              </p>
            )}
            <div className="search-form">
              <input
                type="text"
                className="form-control"
                placeholder={
                  activeQuery
                    ? `Follow up on "${activeQuery}"…`
                    : 'Ask a research question…'
                }
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAgentChat()}
              />
              <button
                className="btn btn-outline-primary"
                onClick={() => handleAgentChat()}
                disabled={isChatting || !chatMessage.trim()}
              >
                {isChatting ? 'Thinking…' : 'Ask follow-up'}
              </button>
            </div>
            {chatError && <p className="research-error">{chatError}</p>}
            {chatTurns.length > 0 && (
              <div className="chat-thread" aria-label="Analyst conversation">
                {chatTurns.map((turn, index) => (
                  <div
                    key={`${turn.role}-${index}`}
                    className={`chat-turn chat-turn-${turn.role}`}
                  >
                    <div className="chat-turn-label">
                      {turn.role === 'user' ? 'You' : 'Analyst'}
                    </div>
                    <div className="chat-turn-body">
                      <ReactMarkdown
                        components={{
                          a: ({ href, children }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer">
                              {children}
                            </a>
                          ),
                        }}
                      >
                        {turn.content}
                      </ReactMarkdown>
                    </div>
                    {turn.role === 'assistant' && turn.citations?.length > 0 && (
                      <div className="agent-citations">
                        <h6>Sources cited</h6>
                        <ul>
                          {turn.citations.map((citation) => (
                            <li key={citation.documentId || citation.title}>
                              {citation.url ? (
                                <a href={citation.url} target="_blank" rel="noopener noreferrer">
                                  {citation.title}
                                </a>
                              ) : (
                                citation.title
                              )}
                              {citation.snippet && (
                                <span className="citation-snippet">{citation.snippet}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {showDraftSection && (
            <section className="research-panel draft-section workspace-panel-draft">
              <div className="panel-label">Step 4 — Editor</div>
              <h3>Draft article</h3>
              <p className="research-hint">
                Choose what the editor sees, generate neutral Markdown, save drafts privately, then publish to your
                author channel and <Link to="/stringers">Stringers</Link>.
              </p>
              <fieldset className="draft-context-toggles">
                <legend>Editor context</legend>
                <label className="draft-toggle">
                  <input
                    type="checkbox"
                    checked={includeArticles}
                    onChange={(e) => setIncludeArticles(e.target.checked)}
                  />
                  Selected RSS articles
                </label>
                <label className="draft-toggle">
                  <input
                    type="checkbox"
                    checked={includeAnalystThread}
                    onChange={(e) => setIncludeAnalystThread(e.target.checked)}
                  />
                  Analyst conversation
                </label>
                <p className="draft-toggle-hint">
                  Analyst thread is sent to the editor agent for this draft only — not shown on your public channel.
                </p>
                <label className="draft-toggle">
                  <input
                    type="checkbox"
                    checked={includeMaterials}
                    onChange={(e) => setIncludeMaterials(e.target.checked)}
                  />
                  Uploaded materials
                </label>
              </fieldset>
              {savedDrafts.length > 0 && (
                <div className="my-drafts-panel">
                  <h4>My drafts</h4>
                  <ul className="my-drafts-list">
                    {savedDrafts.map((d) => (
                      <li key={d.id}>
                        <button
                          type="button"
                          className={`my-drafts-item${currentDraftId === d.id ? ' active' : ''}`}
                          onClick={() => handleLoadDraft(d)}
                        >
                          {d.title || 'Untitled draft'}
                          <span className="my-drafts-date">
                            {d.updatedAt
                              ? formatLocalDate(d.updatedAt)
                              : d.createdAt
                                ? formatLocalDate(d.createdAt)
                                : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="btn btn-sm btn-link" onClick={handleNewDraft}>
                    Start new draft
                  </button>
                </div>
              )}
              <div className="draft-generate-bar">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleDraftArticle}
                  disabled={isDrafting || !draftReady}
                >
                  {isDrafting
                    ? 'Drafting…'
                    : draftContent.trim()
                      ? 'Regenerate full draft'
                      : includeArticles
                        ? `Draft from ${selectedCount} source(s)`
                        : 'Generate draft'}
                </button>
                {lastDraftContext?.includedChatTurnCount > 0 && (
                  <span className="draft-context-meta">
                    Included {lastDraftContext.includedChatTurnCount} analyst turn(s)
                  </span>
                )}
              </div>
              {(draftContent || draftTitle) && (
                <div className="draft-workspace">
                  {citationSources.length > 0 && (
                    <aside className={`citation-sidebar${citationsOpen ? ' open' : ''}`}>
                      <button
                        type="button"
                        className="citation-sidebar-toggle"
                        onClick={() => setCitationsOpen((open) => !open)}
                        aria-expanded={citationsOpen}
                      >
                        Sources ({citationSources.length})
                      </button>
                      <div className="citation-sidebar-body">
                        <h4>Sources in draft</h4>
                        <ul className="citation-list">
                          {citationSources.map((source) => (
                            <li key={source.id}>
                              <Link to={`/article/${source.id}`}>{source.title}</Link>
                              <div className="citation-meta">
                                {source.source}
                                {source.url && (
                                  <>
                                    {' · '}
                                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                                      Original ↗
                                    </a>
                                  </>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </aside>
                  )}
                <div className="draft-editor">
                  <label className="form-label" htmlFor="draft-title">
                    Title
                  </label>
                  <input
                    id="draft-title"
                    className="form-control mb-2"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                  />
                  <div className="draft-editor-split">
                    <div className="draft-editor-pane">
                      <label className="form-label" htmlFor="draft-body">
                        Body (Markdown)
                      </label>
                      <textarea
                        id="draft-body"
                        className="form-control draft-textarea"
                        rows={14}
                        value={draftContent}
                        onChange={(e) => setDraftContent(e.target.value)}
                      />
                    </div>
                    <div className="draft-preview-pane">
                      <div className="form-label">Preview</div>
                      <div className="draft-preview author-markdown">
                        <ReactMarkdown>{normalizeMarkdownContent(draftContent)}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                  <div className="draft-actions">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleSaveDraft}
                      disabled={
                        isSavingDraft || isPublishing || !draftTitle.trim() || !draftContent.trim()
                      }
                    >
                      {isSavingDraft ? 'Saving…' : 'Save draft'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handlePublishDraft}
                      disabled={
                        isPublishing || isSavingDraft || !draftTitle.trim() || !draftContent.trim()
                      }
                    >
                      {isPublishing ? 'Publishing…' : 'Publish to your channel'}
                    </button>
                  </div>
                  {draftSaveMessage && (
                    <p className="draft-save-message" role="status">
                      {draftSaveMessage}
                    </p>
                  )}
                </div>
                </div>
              )}
              {draftError && <p className="research-error">{draftError}</p>}
              {publishMessage && (
                <div className="publish-success" role="status">
                  <p className="publish-success-text">{publishMessage}</p>
                  {publishedArticleUrl ? (
                    <Link to={publishedArticleUrl} className="btn btn-success btn-sm">
                      View on your channel →
                    </Link>
                  ) : (
                    <p className="research-hint mb-0">
                      Set up your author slug on first publish to get a public channel link.
                    </p>
                  )}
                </div>
              )}
            </section>
          )}
          {regenerateConfirmOpen && (
            <div className="research-slug-modal" role="dialog" aria-modal="true">
              <div className="research-slug-modal-card">
                <h4>Regenerate full draft?</h4>
                <p className="research-hint">
                  The editor will run again with your current context toggles. Your manual edits to the
                  title and body will be replaced if you continue.
                </p>
                <div className="whats-new-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmRegenerate}
                    disabled={isDrafting}
                  >
                    {isDrafting ? 'Regenerating…' : 'Regenerate'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setRegenerateConfirmOpen(false)}
                    disabled={isDrafting}
                  >
                    Keep my edits
                  </button>
                </div>
              </div>
            </div>
          )}
          {slugModalOpen && (
            <div className="research-slug-modal" role="dialog" aria-modal="true">
              <div className="research-slug-modal-card">
                <h4>Set up your author channel</h4>
                <p className="research-hint">
                  Choose a public display name and URL slug for your first publish.
                </p>
                <label className="form-label" htmlFor="author-display-name">
                  Display name
                </label>
                <input
                  id="author-display-name"
                  className="form-control mb-2"
                  value={authorDisplayName}
                  onChange={(e) => setAuthorDisplayName(e.target.value)}
                />
                <label className="form-label" htmlFor="author-slug">
                  Channel slug
                </label>
                <input
                  id="author-slug"
                  className="form-control mb-2"
                  value={authorSlugInput}
                  onChange={(e) => setAuthorSlugInput(e.target.value.toLowerCase())}
                  placeholder="e.g. jane-analyst"
                />
                <p className="research-hint">
                  Your channel will be at /authors/{authorSlugInput || 'your-slug'}
                </p>
                <div className="whats-new-actions">
                  {draftTitle.trim() && draftContent.trim() ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConfirmSlugPublish}
                      disabled={isPublishing}
                    >
                      {isPublishing ? 'Publishing…' : 'Publish to channel'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveSlugPreview}
                    >
                      Save — used on first publish
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSlugModalOpen(false)}
                    disabled={isPublishing}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

function stripHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export default ResearchPage;
